import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Output } from "@angular/core";
import { ZXingScannerModule } from "@zxing/ngx-scanner";
import { BehaviorSubject } from "rxjs";
import Swal from "sweetalert2";
import { SmartLabelPipe } from "../../shared/pipes/smartlabel.pipe";

@Component({
  selector: 'attendance-add-by-scanner',
  standalone: true,
  imports: [CommonModule, ZXingScannerModule, SmartLabelPipe],
  providers: [],
  templateUrl: './attendance-add-by-scanner.component.html'
})
export class AttendanceAddByScannerComponent {
    @Output() public scanComplete = new EventEmitter<string>();

    protected show: boolean = false;
    private readonly localStorageKey = 'CAMERA_DEVICE_ID';
    protected hasStarted: boolean = false;

    private torchAvailable$ = new BehaviorSubject<boolean>(false);
    protected torchAvailable = this.torchAvailable$.asObservable();
    protected torchEnabled: boolean = false;
    
    private currentCameraIndex: number = 0;
    protected availableCameras?: MediaDeviceInfo[] | null;
    protected currentCamera: MediaDeviceInfo | undefined;
    protected hasCameraPermission?: boolean;

    get canSwitchCameras(): boolean {
        return !!this.availableCameras
            && this.availableCameras.length > 1;
    }

    get canUseCamera(): boolean {
        return this.hasStarted
            && !!this.availableCameras
            && this.hasCameraPermission === true;
    }

    get showErrorMessage(): boolean {
        return this.availableCameras === null
            || this.hasCameraPermission === false;
    }

    get lastCameraUsed(): string | null {
        return localStorage.getItem(this.localStorageKey);
    }

    set lastCameraUsed(deviceId: string) {
        localStorage.setItem(this.localStorageKey, deviceId);
    }

    showSlider = (): void => {
        this.show = true;
        this.torchEnabled = false;
        document.body.classList.add('no-scroll');
    }

    closeSlider = (): void => {
        this.show = false;
        this.torchEnabled = false;
        if (document.body.classList.contains('no-scroll')) {
            document.body.classList.remove('no-scroll');
        }
    }

    onScanSuccess = (result: string): void => {
        this.scanComplete.emit(result);
        this.closeSlider();
    }

    onScanError = (error: Error): void => {
        Swal.fire({
            title: 'Invalid QR code',
            text: 'The QR code is invalid',
            icon: 'error',
            confirmButtonText: 'Ok'
        });
    }

    onStarted(): void {
        if (this.availableCameras) {
            const lastCameraIndex = this.availableCameras.findIndex(camera => camera.deviceId === this.lastCameraUsed);
            this.currentCameraIndex = lastCameraIndex !== -1 ? lastCameraIndex : this.availableCameras.length - 1;
            this.currentCamera = this.availableCameras[this.currentCameraIndex];
        }
        this.hasStarted = true;
    }

    onCamerasFound(cameras: MediaDeviceInfo[]): void {
        if (cameras.length > 2) {
            let frontCameraNames = [
                // iOS reports camera labels in the device language (for some languages)
                'Front Camera',
                'Cámara frontal',
                
                // Android reports camera labels by id and direction in english
                'camera 1, facing front'
            ];
            let backCameraNames = [
                // iOS
                'Back Camera',
                'Cámara trasera',
                
                // Android
                'camera 0, facing back'
            ];

            let frontCamera = cameras.find(camera => frontCameraNames.includes(camera.label));
            let backCamera = cameras.find(camera => backCameraNames.includes(camera.label));

            // If both front and rear cameras were identified, use them. Otherwise, use all
            this.availableCameras = frontCamera && backCamera ? [frontCamera, backCamera] : cameras;
        } else {
            this.availableCameras = cameras;
        }
    }

    onCamerasNotFound(): void {
        this.availableCameras = null;
    }

    onPermissionResponse(hasPermission: boolean) {
        this.hasCameraPermission = hasPermission;
    }

    onTorchCompatible = (isCompatible: boolean): void => {
        this.torchAvailable$.next(isCompatible);
    }

    onTorchClicked = (): void => {
        this.torchEnabled = !this.torchEnabled;
    }

    onSwitchCameraClicked = (): void => {
        this.currentCameraIndex = (this.currentCameraIndex + 1) % this.availableCameras!.length;
        this.currentCamera = this.availableCameras![this.currentCameraIndex];
        this.lastCameraUsed = this.currentCamera.deviceId;
        this.torchEnabled = false;
    }

    onCloseSliderClicked = (): void => {
        this.closeSlider();
    }
}